import React, { useEffect, useState } from 'react';
import { financeiroAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

/**
 * Modal simplificado de Despesa.
 * Campos: Categoria (topo), Valor, Data, [Serviço], Observações.
 * (Descrição e Forma de Pagamento removidos do form — descrição é assumida do nome da categoria no backend.)
 */
export default function ExpenseFormModal({
  editing,
  categories: catProp,
  defaultServicoId = '',
  lockServico = false,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(() => editing ? {
    valor: editing.valor || 0,
    data: editing.data || new Date().toISOString().slice(0, 10),
    categoria_id: editing.categoria_id || '',
    servico_id: editing.servico_id || defaultServicoId || '',
    observacoes: editing.observacoes || '',
  } : {
    valor: 0,
    data: new Date().toISOString().slice(0, 10),
    categoria_id: '',
    servico_id: defaultServicoId || '',
    observacoes: '',
  });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(catProp || []);
  const [services, setServices] = useState([]);
  const [loadingSvcs, setLoadingSvcs] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatNome, setNewCatNome] = useState('');
  const [newCatCor, setNewCatCor] = useState('#ef4444');
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    // Sempre recarrega categorias quando o modal abre (mantém sync com cadastros novos)
    if (!catProp || !catProp.length) {
      financeiroAPI.listCategories('despesa').then(r => setCategories(r.data || [])).catch(() => {});
    }
  }, [catProp]);

  useEffect(() => {
    if (lockServico) return;
    setLoadingSvcs(true);
    financeiroAPI.listServices({ em_andamento: true })
      .then(r => {
        let lst = r.data || [];
        const sid = editing?.servico_id || defaultServicoId;
        if (sid && !lst.find(s => s.id === sid)) {
          return financeiroAPI.getService(sid).then(rr => {
            if (rr.data) lst = [rr.data, ...lst];
            setServices(lst);
          }).catch(() => setServices(lst));
        }
        setServices(lst);
      })
      .catch(() => setServices([]))
      .finally(() => setLoadingSvcs(false));
  }, [editing?.servico_id, defaultServicoId, lockServico]);

  const handleCreateCategoria = async () => {
    if (!newCatNome.trim()) { toast.error('Informe o nome da categoria'); return; }
    setSavingCat(true);
    try {
      const r = await financeiroAPI.createCategory({
        nome: newCatNome.trim(),
        tipo: 'despesa',
        cor: newCatCor,
        ativo: true,
      });
      const novaCat = r.data;
      setCategories(prev => [...prev, novaCat].sort((a, b) => a.nome.localeCompare(b.nome)));
      setForm(f => ({ ...f, categoria_id: novaCat.id }));
      setShowNewCat(false);
      setNewCatNome('');
      toast.success('Categoria criada');
    } catch (e) {
      toast.error('Erro ao criar categoria');
    } finally {
      setSavingCat(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.data) { toast.error('Data obrigatória'); return; }
    if (!form.valor || parseFloat(form.valor) <= 0) { toast.error('Informe um valor maior que zero'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor: parseFloat(form.valor) || 0,
        servico_id: form.servico_id || null,
      };
      if (editing) {
        await financeiroAPI.updateExpense(editing.id, payload);
      } else {
        await financeiroAPI.createExpense(payload);
      }
      toast.success('Despesa salva');
      onSaved && onSaved();
    } catch (e) {
      toast.error('Erro ao salvar despesa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dialog-despesa">
        <DialogHeader>
          <DialogTitle>{editing ? 'Editar Despesa' : 'Nova Despesa'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Categoria (no topo) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label>Categoria *</Label>
              <button
                type="button"
                className="text-[11px] text-cyan-600 hover:underline inline-flex items-center gap-1"
                onClick={() => setShowNewCat(s => !s)}
                data-testid="btn-toggle-nova-categoria"
              >
                <Plus className="w-3 h-3" /> {showNewCat ? 'cancelar' : 'nova'}
              </button>
            </div>
            <Select
              value={form.categoria_id || 'none'}
              onValueChange={(v) => {
                const catId = v === 'none' ? '' : v;
                setForm(f => {
                  // Pré-preenche valor com valor_padrao se estiver zerado e a categoria tiver valor_padrao > 0
                  const cat = categories.find(c => c.id === catId);
                  const valAtual = parseFloat(f.valor) || 0;
                  const valorPadrao = cat?.valor_padrao || 0;
                  const novoValor = valAtual === 0 && valorPadrao > 0 ? valorPadrao : f.valor;
                  return { ...f, categoria_id: catId, valor: novoValor };
                });
              }}
            >
              <SelectTrigger data-testid="desp-categoria-trigger">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nenhuma —</SelectItem>
                {categories.filter(c => c.ativo !== false).map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {showNewCat && (
              <div className="mt-2 p-2 border rounded-lg bg-muted/30 space-y-2" data-testid="form-nova-categoria">
                <div className="flex gap-2">
                  <Input
                    placeholder="Nome da nova categoria"
                    value={newCatNome}
                    onChange={(e) => setNewCatNome(e.target.value)}
                    data-testid="input-nova-cat-nome"
                  />
                  <Input
                    type="color"
                    value={newCatCor}
                    onChange={(e) => setNewCatCor(e.target.value)}
                    className="w-14 p-1 cursor-pointer"
                    title="Cor"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleCreateCategoria}
                  disabled={savingCat}
                  className="w-full"
                  data-testid="btn-criar-categoria"
                >
                  {savingCat ? 'Criando...' : 'Criar categoria'}
                </Button>
              </div>
            )}
          </div>

          {/* Valor e Data */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Valor (R$) *</Label>
              <Input
                type="number" step="0.01"
                value={form.valor}
                onChange={(e) => setForm(f => ({ ...f, valor: e.target.value }))}
                data-testid="desp-valor"
              />
            </div>
            <div>
              <Label>Data *</Label>
              <Input
                type="date"
                value={form.data}
                onChange={(e) => setForm(f => ({ ...f, data: e.target.value }))}
                data-testid="desp-data"
              />
            </div>
          </div>

          {/* Vincular Serviço (oculto quando lockServico) */}
          {!lockServico && (
            <div>
              <Label>Vincular a Serviço (opcional)</Label>
              <Select
                value={form.servico_id || 'none'}
                onValueChange={(v) => setForm(f => ({ ...f, servico_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger data-testid="desp-servico-trigger">
                  <SelectValue placeholder={loadingSvcs ? 'Carregando...' : 'Selecione um serviço'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sem vínculo —</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.titulo} {s.cliente_nome ? `· ${s.cliente_nome}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Observação */}
          <div>
            <Label>Observação</Label>
            <Textarea
              rows={3}
              value={form.observacoes}
              onChange={(e) => setForm(f => ({ ...f, observacoes: e.target.value }))}
              placeholder="Detalhes adicionais (opcional)"
              data-testid="desp-observacoes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="btn-save-despesa">
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
